import { useMemo } from 'react'

import type { FlightPlayerScoresFragment } from '../../../__generated__/hooks'
import { groupByTeam } from '../../../util/best-ball'
import PlayerScoreCard from '../components/PlayerScoreCard'

/**
 * Team-grouped score entry. Each team gets its own card-list block with
 * a header (team name + running pts) and a footer ("Best på hull N: X
 * · Y pts" once everyone scored, otherwise "Venter på alle slag…").
 *
 * The contributing player's card gets the gold ring + 🏆 pill via
 * `usedByTeam` so the captain sees whose shot won the hole at a glance.
 */
const BestBallLayout = ({
  flight,
  currentHole,
  showChips,
  isNet,
  partialCard,
  isYourEntry,
  onSetScore,
  saving,
}: {
  flight: FlightPlayerScoresFragment[]
  currentHole: number
  showChips: boolean
  isNet: boolean
  partialCard?: boolean
  isYourEntry: (entryId: string) => boolean
  onSetScore: (scoreId: string, gross: number) => void
  saving: boolean
}) => {
  const teamGroups = useMemo(
    () => groupByTeam(flight, currentHole),
    [flight, currentHole]
  )

  return (
    <div className="flex flex-col gap-3 px-2 mt-3">
      {teamGroups.map((g) => {
        const allScoredOnHoleInTeam = g.members.every(
          (m) =>
            (m.scores ?? []).find((s) => s.hole.number === currentHole)
              ?.score != null
        )
        return (
          <section
            key={g.teamId}
            className="flex flex-col gap-2 p-2 bg-ink-100 rounded-lg"
            aria-label={`Lag ${g.teamName}`}
          >
            <header className="flex items-center justify-between px-2 pt-1">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-700 truncate">
                Lag · {g.teamName}
              </div>
              <div className="text-xs font-mono num font-semibold text-ink-900">
                {g.teamAccumulatedStableford} pts
              </div>
            </header>
            {g.members.map((entry) => (
              <PlayerScoreCard
                key={entry.id}
                entry={entry}
                holeNumber={currentHole}
                isYou={isYourEntry(entry.id)}
                showChips={showChips}
                isNet={isNet}
                partialCard={partialCard}
                usedByTeam={g.bestForHole.contributorId === entry.id}
                onSetScore={onSetScore}
                saving={saving}
              />
            ))}
            <footer className="px-2 pb-1 text-[11px] text-ink-700">
              {allScoredOnHoleInTeam && g.bestForHole.score != null ? (
                <span>
                  Best på hull {currentHole}:{' '}
                  <span className="font-mono num font-bold text-ink-900">
                    {g.bestForHole.score}
                  </span>
                  {g.bestForHole.stablefordPoints != null ? (
                    <span className="ml-1 text-ink-500">
                      · {g.bestForHole.stablefordPoints} pts
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="text-ink-500 italic">
                  Venter på alle slag fra laget…
                </span>
              )}
            </footer>
          </section>
        )
      })}
    </div>
  )
}

export default BestBallLayout
